/**
 * Эндпоинты аутентификации и приглашений — по HTTP, поэтому у веба свои.
 * Текущий пользователь (me.get, по сокету) — в @taskflow/client-core.
 *
 * Вход отдаёт access-токен, который кладётся в память (не в localStorage,
 * §4.2), и профиль с эффективными правами. Refresh-токен сервер ставит
 * cookie сам — клиент его не видит.
 */

import type { Permission } from "@taskflow/contracts";

import { api, type CurrentUser } from "@taskflow/client-core";
import {
  httpAcceptInvitation,
  httpConfirmPasswordReset,
  httpDeclineInvitation,
  httpInvitation,
  httpLogin,
  httpLogout,
  httpRequestPasswordReset,
} from "@/shared/auth/httpAuth";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SessionPayload {
  accessToken: string;
  expiresIn: number;
  user: CurrentUser;
  permissions: Permission[];
}

export interface InvitationPreview {
  status: "PENDING" | "ACCEPTED" | "DECLINED" | "REVOKED" | "EXPIRED";
  email: string;
  departmentName: string | null;
  boardName: string | null;
  projectName: string | null;
  roleName: string | null;
  inviterName: string | null;
  expiresAtLabel: string | null;
  /** Нужно ли задать пароль — у нового пользователя учётной записи ещё нет. */
  requiresRegistration: boolean;
}

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Вход и выход идут по HTTP, а не по сокету: только ответ HTTP
    // может поставить и стереть HttpOnly-cookie с refresh-токеном (§4.2).
    // Эндпоинты остаются здесь, чтобы состояние загрузки и ошибки
    // обрабатывались единообразно со всеми остальными.
    login: build.mutation<SessionPayload, LoginRequest>({
      queryFn: (credentials) => httpLogin<SessionPayload>(credentials),
    }),

    logout: build.mutation<{ ok: boolean }, void>({
      queryFn: async () => {
        await httpLogout();
        return { data: { ok: true } };
      },
    }),

    requestPasswordReset: build.mutation<{ ok: boolean }, { email: string }>({
      // Ответ всегда нейтральный: §4.3 запрещает раскрывать наличие учётной записи.
      // По HTTP: «Забыли пароль?» нажимает гость, у которого нет сессии.
      queryFn: ({ email }) => httpRequestPasswordReset<{ ok: boolean }>(email),
    }),

    confirmPasswordReset: build.mutation<
      { ok: boolean },
      { token: string; password: string }
    >({
      queryFn: ({ token, password }) => httpConfirmPasswordReset<{ ok: boolean }>(token, password),
    }),

    invitation: build.query<InvitationPreview, string>({
      // По HTTP: ссылку из письма открывает гость — WebSocket без сессии не работает.
      queryFn: (token) => httpInvitation<InvitationPreview>(token),
    }),

    acceptInvitation: build.mutation<
      SessionPayload,
      {
        token: string;
        password?: string;
        firstName?: string;
        lastName?: string;
        middleName?: string;
        position?: string;
      }
    >({
      // По HTTP: приём заводит сессию, cookie ставит только ответ HTTP.
      queryFn: ({ token, ...profile }) =>
        httpAcceptInvitation<SessionPayload>(token, profile),
    }),

    declineInvitation: build.mutation<{ ok: boolean }, { token: string }>({
      queryFn: ({ token }) => httpDeclineInvitation<{ ok: boolean }>(token),
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useRequestPasswordResetMutation,
  useConfirmPasswordResetMutation,
  useInvitationQuery,
  useAcceptInvitationMutation,
  useDeclineInvitationMutation,
} = authApi;
